/* eslint-disable @typescript-eslint/no-unused-vars */
import { getInseeCodeFromLegalForm } from '@shinetools/legal-form-library';
import { isNil, pick, pipe, reject } from 'ramda';
import { assign } from 'xstate';

import assertEventType from 'common/bento/lib/assertEventType';
import { MachineActions } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import { TrackSegmentEventDocument } from 'features/Analytics/graphql/mutations/trackSegmentEvent.gql';
import getIsEi from 'features/Onboarding/lib/getIsEi';

import companyProfileStorage from '../companyProfileStorage';
import { getLegalForm, isMicroCompany } from '../utils';
import {
  CompanyProfileInputState,
  initialCompanyProfile,
  Model,
} from './model';

export enum Action {
  SaveActivityDetails = 'saveActivityDetails',
  SaveIdentityInfo = 'saveIdentityInfo',
  SaveLegalInfo = 'saveLegalInfo',
  SaveBusinessForm = 'saveBusinessForm',
  SaveIsMicro = 'saveIsMicro',
  SaveExternalCompanyInfo = 'saveExternalCompanyInfo',
  SkipExternalCompany = 'skipExternalCompany',
  ResetCompanyInfo = 'resetCompanyInfo',
  RestoreCompanyInfo = 'restoreCompanyInfo',
}

const actions: MachineActions<Model, Action> = {
  resetCompanyInfo: assign({
    companyProfile: (context) => {
      const newState = initialCompanyProfile;

      companyProfileStorage.clear();

      return newState;
    },
  }),

  restoreCompanyInfo: assign({
    companyProfile: (context) =>
      companyProfileStorage.get() ?? initialCompanyProfile,
  }),

  saveActivityDetails: assign({
    companyProfile: ({ companyProfile }, event) => {
      assertEventType(event, 'SUBMIT_APE_DETAILS');

      const newState: CompanyProfileInputState = {
        ...companyProfile,
        activityDetails: event.data,
      };

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  saveBusinessForm: assign({
    companyProfile: ({ companyProfile }, event) => {
      assertEventType(event, 'SUBMIT_BUSINESS_FORM');

      const businessForm = event.data;
      const legalForm = getLegalForm(businessForm);

      const newState: CompanyProfileInputState = {
        ...companyProfile,
        inseeLegalFormCode: getInseeCodeFromLegalForm(legalForm) || '',
        isMicro: isMicroCompany(businessForm),
        legalForm,
        ...(legalForm === 'EI' ? { legalNumberOfEmployeeRange: '0' } : {}),
      };

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  saveExternalCompanyInfo: assign({
    companyProfile: (context, event): CompanyProfileInputState => {
      assertEventType(event, 'SUBMIT_EXTERNAL_COMPANY');

      const companyData = event.data;
      const {
        activityType,
        city,
        legalForm,
        legalName,
        legalRegistrationNumber,
        registrationDate,
        street,
        zip,
      } = companyData;

      const withValidCompanyData = reject(isNil, companyData);
      const initialKeys = Object.keys(initialCompanyProfile);
      const overrides = pick(initialKeys, withValidCompanyData);

      const newState: CompanyProfileInputState = {
        ...initialCompanyProfile,
        ...overrides,
        isExternal: true,
      };

      if (!getIsEi({ legalForm: legalForm ?? '' })) {
        newState.isMicro = false;
      }

      apolloClient.mutate({
        mutation: TrackSegmentEventDocument,
        variables: {
          input: {
            event: 'Company Siret Submitted',
            properties: {
              hasIdentityData: Boolean(
                legalName && street && zip && street && city,
              ),
              hasLegalData: Boolean(
                activityType && legalRegistrationNumber && registrationDate,
              ),
            },
          },
        },
      });

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  saveIdentityInfo: assign({
    companyProfile: ({ companyProfile }, event) => {
      assertEventType(event, 'SUBMIT_IDENTITY_INFO');

      const { tradeName, ...data } = event.data;

      const newState: CompanyProfileInputState = {
        ...companyProfile,
        ...data,
        tradeName: tradeName ?? '',
      };

      apolloClient.mutate({
        mutation: TrackSegmentEventDocument,
        variables: {
          input: {
            event: 'Company Identity Info Updated',
          },
        },
      });

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  saveIsMicro: assign({
    companyProfile: ({ companyProfile }, event) => {
      assertEventType(event, 'SUBMIT_IS_MICRO');

      const newState: CompanyProfileInputState = {
        ...companyProfile,
        isMicro: event.data,
        legalNumberOfEmployeeRange: '0',
      };

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  saveLegalInfo: assign({
    companyProfile: ({ companyProfile }, event) => {
      assertEventType(event, 'SUBMIT_LEGAL_INFO');

      const newState: CompanyProfileInputState = {
        ...companyProfile,
        ...event.data,
        activityDetails: '',
      };

      apolloClient.mutate({
        mutation: TrackSegmentEventDocument,
        variables: {
          input: {
            event: 'Company Legal Info Updated',
          },
        },
      });

      companyProfileStorage.set(newState);

      return newState;
    },
  }),

  skipExternalCompany: () => {
    apolloClient.mutate({
      mutation: TrackSegmentEventDocument,
      variables: {
        input: {
          event: 'Company Search Manual Option Taken',
        },
      },
    });
  },
};

export default actions;
